import { Api } from '../../redux/api/axiosInstance';
import type { AxiosResponse } from 'axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OfferComponentInput {
  componentTypeId?: number | null;
  label: string;
  amount: number;
}

export interface OfferBenefitInput {
  label: string;
  description?: string;
}

export interface CreateOfferPayload {
  candidate_id: number;
  job_id?: number;
  position_title?: string;
  salary_amount?: number;
  currency?: string;
  pay_frequency?: string;
  grade?: string;
  joining_date?: string;
  valid_till?: string;
  terms?: string;
  components?: OfferComponentInput[];
  benefits?: OfferBenefitInput[];
}

export interface OfferSalaryComponent {
  component_id: number;
  offer_id: number;
  component_type_id: number | null;
  label: string;
  amount: number;
  sort_order: number;
}

export interface OfferBenefit {
  benefit_id: number;
  offer_id: number;
  label: string;
  description: string | null;
  sort_order: number;
}

export type OfferStatus =
  | 'Draft'
  | 'Pending Approval'
  | 'Approved'
  | 'Sent'
  | 'Viewed'
  | 'Accepted'
  | 'Declined'
  | 'On Hold'
  | 'Rejected';

export interface CandidateOffer {
  offer_id: number;
  candidate_id: number;
  job_id: number | null;
  OrganizationID: number;
  version: number;
  status: OfferStatus;
  position_title: string | null;
  salary_amount: number | null;
  currency: string;
  pay_frequency: string;
  grade: string | null;
  joining_date: string | null;
  valid_till: string | null;
  terms: string | null;
  decline_reason: string | null;
  decline_action: string | null;
  created_by: number | null;
  approved_by: number | null;
  sent_at: string | null;
  viewed_at: string | null;
  responded_at: string | null;
  pdf_url: string | null;
  salary_in_range: boolean;
  created_at: string;
  updated_at: string;
  components?: OfferSalaryComponent[];
  benefits?: OfferBenefit[];
}

export interface OfferActivityLog {
  log_id: number;
  offer_id: number;
  actor_id: number | null;
  action: string | null;
  from_status: string | null;
  to_status: string | null;
  notes: string | null;
  created_at: string;
}

export interface DeclineOfferPayload {
  decline_reason?: string;
  decline_action: 'counter_offer' | 'extend_deadline' | 'reject' | 'on_hold';
  new_valid_till?: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────
// NOTE: All responses are wrapped by TransformInterceptor as:
//   { success: boolean; statusCode: number; data: T; timestamp: string }
// So axios r.data = envelope → we need r.data.data to get the actual payload.

type Envelope<T> = { success: boolean; data: T; statusCode: number; timestamp: string };

/** Unwrap a single-object response from the TransformInterceptor envelope. */
const unwrap = <T>(r: AxiosResponse<Envelope<T> | T>): T => {
  const body = r.data;
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as Envelope<T>).data;
  }
  return body as T;
};

/** Unwrap an array response from the TransformInterceptor envelope. */
const unwrapArray = <T>(r: AxiosResponse<Envelope<T[]> | T[]>): T[] => {
  const body = r.data;
  let d: unknown;
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    d = (body as Envelope<T[]>).data;
  } else {
    d = body;
  }
  return Array.isArray(d) ? d : [];
};

export const createOffer = (payload: CreateOfferPayload): Promise<CandidateOffer> =>
  Api.post('/offers', payload).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const getOffersByCandidate = (candidateId: number): Promise<CandidateOffer[]> =>
  Api.get(`/offers/candidate/${candidateId}`).then((r: AxiosResponse<any>) => unwrapArray<CandidateOffer>(r));

export const getOfferById = (offerId: number): Promise<CandidateOffer> =>
  Api.get(`/offers/${offerId}`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

/** Rendered offer-letter HTML for the recruiter preview (same render the candidate gets). */
export const getOfferPreview = (offerId: number): Promise<{ html: string }> =>
  Api.get(`/offers/${offerId}/preview`).then((r: AxiosResponse<any>) => unwrap<{ html: string }>(r));

export const getOfferHistory = (offerId: number): Promise<OfferActivityLog[]> =>
  Api.get(`/offers/${offerId}/history`).then((r: AxiosResponse<any>) => unwrapArray<OfferActivityLog>(r));

export const submitForApproval = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/submit`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const approveOffer = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/approve`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const rejectApproval = (offerId: number, comments?: string): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/reject-approval`, { comments }).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const sendOffer = (
  offerId: number,
  opts?: { pdfTemplateId?: number; emailTemplateId?: number },
): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/send`, opts ?? {}).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

/** Download the filled offer-letter PDF for a chosen template. */
export const downloadOfferLetter = async (
  offerId: number,
  templateId: number,
  candidateName?: string,
): Promise<void> => {
  const res = await Api.get(`/offers/${offerId}/letter.pdf?templateId=${templateId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Offer_Letter_${(candidateName || 'Candidate').replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const acceptOffer = (offerId: number): Promise<CandidateOffer> =>
  Api.patch(`/offers/${offerId}/accept`).then((r: AxiosResponse<any>) => unwrap<CandidateOffer>(r));

export const declineOffer = (
  offerId: number,
  payload: DeclineOfferPayload,
): Promise<{ success: boolean; message: string; newOfferId?: number }> =>
  Api.patch(`/offers/${offerId}/decline`, payload).then(
    (r: AxiosResponse<any>) => unwrap<{ success: boolean; message: string; newOfferId?: number }>(r),
  );
